import { MapPin, Phone, Clock, Mail } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useBrand } from "@/contexts/BrandContext";
import { usePublicBranches } from "@/hooks/usePublicBranches";
import type { AboutInfo } from "@/hooks/usePublicAbout";

interface Props { about: AboutInfo | null; }

export default function VisitSection({ about }: Props) {
  const { language } = useLanguage();
  const { localizedName } = useBrand();
  const { branches } = usePublicBranches();

  return (
    <section className="py-20 md:py-28 bg-foreground text-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="font-heading text-4xl md:text-6xl font-black text-background">
            {language === "ar" ? `زور ${localizedName("ar")}` : `Visit ${localizedName("en")}`}
          </h2>
          <p className="text-background/50 mt-3">
            {language === "ar" ? "واكتشف تجربة لا تُنسى!" : "And discover an unforgettable experience!"}
          </p>
          <div className="w-16 h-1 bg-primary mx-auto mt-4 rounded-full" />
        </div>

        {branches.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {branches.map((branch) => (
              <div
                key={branch.id}
                className="rounded-2xl border border-background/10 bg-background/5 backdrop-blur p-6 space-y-3 hover:bg-background/10 transition-all"
              >
                <h3 className="font-heading font-bold text-lg text-background">
                  {language === "ar" ? branch.nameAr || branch.name : branch.name}
                </h3>
                {(language === "ar" ? branch.addressAr || branch.address : branch.address) && (
                  <div className="flex items-start gap-2 text-sm text-background/60">
                    <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
                    <span>{language === "ar" ? branch.addressAr || branch.address : branch.address}</span>
                  </div>
                )}
                {branch.phone && (
                  <div className="flex items-center gap-2 text-sm text-background/60">
                    <Phone className="w-4 h-4 shrink-0 text-primary" />
                    <a href={`tel:${branch.phone}`} className="hover:text-primary transition-colors">{branch.phone}</a>
                  </div>
                )}
                {(language === "ar" ? branch.workingHoursAr || branch.workingHours : branch.workingHours) && (
                  <div className="flex items-center gap-2 text-sm text-background/60">
                    <Clock className="w-4 h-4 shrink-0 text-primary" />
                    <span>{language === "ar" ? branch.workingHoursAr || branch.workingHours : branch.workingHours}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {(about?.contactPhone || about?.contactEmail) && (
          <div className="flex flex-wrap justify-center gap-6 mt-10 text-sm text-background/50">
            {about.contactPhone && (
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-primary" />
                <a href={`tel:${about.contactPhone}`} className="hover:text-primary transition-colors">{about.contactPhone}</a>
              </div>
            )}
            {about.contactEmail && (
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-primary" />
                <a href={`mailto:${about.contactEmail}`} className="hover:text-primary transition-colors">{about.contactEmail}</a>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
